/**
 * Pure spectrum selection and transformation helpers shared by the app shell
 * and the characterisation tests. No React, no DOM.
 */

import { spectrumAt } from "./loader";
import type { EpochRecord, SpectraPackage, SpectralState } from "./types";

export interface InterpolationResult {
  primaryIndex: number;
  comparisonForBlendIndex: number | null;
  blend: number;
  flux: Float32Array;
}

export function snapToNearestEpoch(epochs: EpochRecord[], mjd: number): number {
  let bestIndex = 0;
  let bestDistance = Infinity;
  for (const epoch of epochs) {
    const distance = Math.abs(epoch.mjd - mjd);
    if (distance < bestDistance) {
      bestDistance = distance;
      bestIndex = epoch.index;
    }
  }
  return bestIndex;
}

export function interpolateAt(
  epochs: EpochRecord[],
  pkg: SpectraPackage,
  mjd: number,
): InterpolationResult {
  const sorted = [...epochs].sort((a, b) => a.mjd - b.mjd);
  if (mjd <= sorted[0].mjd) {
    return {
      primaryIndex: sorted[0].index,
      comparisonForBlendIndex: null,
      blend: 0,
      flux: spectrumAt(pkg, sorted[0].index).slice(),
    };
  }
  const last = sorted[sorted.length - 1];
  if (mjd >= last.mjd) {
    return {
      primaryIndex: last.index,
      comparisonForBlendIndex: null,
      blend: 0,
      flux: spectrumAt(pkg, last.index).slice(),
    };
  }
  for (let i = 0; i < sorted.length - 1; i += 1) {
    const a = sorted[i];
    const b = sorted[i + 1];
    if (mjd >= a.mjd && mjd <= b.mjd) {
      const blend = (mjd - a.mjd) / (b.mjd - a.mjd);
      const fluxA = spectrumAt(pkg, a.index);
      const fluxB = spectrumAt(pkg, b.index);
      const out = new Float32Array(fluxA.length);
      for (let k = 0; k < fluxA.length; k += 1) {
        out[k] = fluxA[k] * (1 - blend) + fluxB[k] * blend;
      }
      return {
        primaryIndex: blend < 0.5 ? a.index : b.index,
        comparisonForBlendIndex: blend < 0.5 ? b.index : a.index,
        blend: blend < 0.5 ? blend : 1 - blend,
        flux: out,
      };
    }
  }
  return {
    primaryIndex: sorted[0].index,
    comparisonForBlendIndex: null,
    blend: 0,
    flux: spectrumAt(pkg, sorted[0].index).slice(),
  };
}

export function applyExtinction(
  flux: Float32Array,
  curve: Float32Array,
  av: number,
): Float32Array {
  const out = new Float32Array(flux.length);
  for (let i = 0; i < flux.length; i += 1) {
    out[i] = flux[i] * Math.pow(10, -0.4 * av * curve[i]);
  }
  return out;
}

export function findEpochByState(epochs: EpochRecord[], state: SpectralState): EpochRecord {
  return epochs.find((e) => e.state === state) ?? epochs[0];
}
